<template>
  <div>
    <div>
      <form @submit.prevent="onSubmit">
        <div class="box1 relative">
          <div class="mb-4">
            <label for="orderNumber">Order Number</label>
            <input
              name="orderNumber"
              id="orderNumber"
              class="regular full bordered"
              type="text"
              v-model="payment.orderNumber"
              :disabled="step !== 1"
            />
          </div>
          <div class="mb-4">
            <label for="customerEmail">Customer Email</label>
            <input
              name="customerEmail"
              id="customerEmail"
              class="regular full bordered"
              type="email"
              v-model="payment.customerEmail"
              :disabled="step !== 1"
            />
          </div>
          <div class="mb-4">
            <label for="customerName">Customer Name</label>
            <input
              required
              name="customerName"
              id="customerName"
              class="regular full bordered"
              type="text"
              v-model="payment.customerName"
              :disabled="step !== 1"
            />
          </div>
          <div class="mb-4">
            <label for="amount">Total Amount</label>
            <input
              required
              name="amount"
              id="amount"
              class="regular full bordered"
              type="number"
              step="0.01"
              v-model="payment.amount"
              :disabled="step !== 1"
              min="0.01"
            />
          </div>
          <div class="mb-4 flex items-center gap-2">
            <input
              type="checkbox"
              id="applyFee"
              v-model="payment.applyFee"
              :disabled="step !== 1"
            />
            <label for="applyFee" class="mb-0">Apply payment fee</label>
          </div>
        </div>
        <div class="box2 relative">
          <div class="mb-4">
            <label for="sellerId">Seller</label>
            <select
              required
              name="sellerId"
              class="regular full bordered"
              v-model="payment.sellerId"
              :disabled="step !== 1"
            >
              <option value="">Select seller</option>
              <option
                v-for="seller in sellers"
                :key="seller.id"
                :value="seller.id"
                >{{ seller.name }}
              </option>
            </select>
          </div>
          <div class="mb-4">
            <label for="description">Description</label>
            <input
              name="description"
              id="description"
              class="regular full bordered"
              type="text"
              v-model="payment.description"
              :disabled="step !== 1"
            />
          </div>
          <button
            v-if="step == 1"
            @click="startPayment()"
            class="btn btn btn-dark-button mt-4 left-auto bottom-2"
            type="submit"
          >
            Confirm, show payment form
          </button>
        </div>
      </form>

      <button
        v-if="step == 2"
        class="btn btn btn-dark-button mt-4 left-auto bottom-2"
        type="button"
        @click="modifyPayment()"
      >
        Back, modify payment details
      </button>

      <div
        v-if="stripeError && step == 1"
        class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mt-4"
      >
        {{ stripeError }}
      </div>

      <div v-if="step == 2" class="mt-4" style="max-width: 500px;">
        <div v-if="feeAmount > 0" class="bg-gray-100 rounded p-3 mb-3 text-sm">
          <div class="flex justify-between">
            <span>Subtotal</span>
            <span>${{ payment.amount }}</span>
          </div>
          <div class="flex justify-between text-orange-600">
            <span>Payment fee</span>
            <span>+${{ feeAmount.toFixed(2) }}</span>
          </div>
          <div class="flex justify-between font-bold border-t mt-1 pt-1">
            <span>Total charged</span>
            <span>${{ totalWithFee.toFixed(2) }}</span>
          </div>
        </div>
        <div v-if="loadingStripe" class="flex items-center gap-2 py-4">
          <BaseMiniSpinner />
          <span>Initializing payment...</span>
        </div>

        <div v-else-if="paymentSuccess" class="text-center py-8">
          <div class="text-5xl mb-3">✓</div>
          <h3 class="text-xl font-bold text-green-600">Payment Successful!</h3>
        </div>

        <StripeCardSection
          v-else-if="stripe && clientSecret"
          :stripe="stripe"
          :clientSecret="clientSecret"
          :amount="payment.amount"
          :customerEmail="payment.customerEmail"
          :billingAddress="{ name: payment.customerName }"
          @confirmed="onPaymentConfirmed"
        >
          <template v-slot:cancel>
            <button
              type="button"
              class="px-6 py-3 border border-gray-300 rounded-md font-semibold hover:bg-gray-100"
              @click="modifyPayment"
            >
              Cancel
            </button>
          </template>
        </StripeCardSection>

        <div
          v-if="stripeError"
          class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mt-4"
        >
          {{ stripeError }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { loadStripe } from "@stripe/stripe-js";
import axios from "axios";
import StripeCardSection from "@/components/payments/StripeCardSection.vue";

export default {
  name: "BasicPosPayment",
  components: { StripeCardSection },
  data() {
    return {
      step: 1,
      payment: {
        orderSource: "phone",
        sellerId: "",
        customerEmail: "",
        description: "",
        orderNumber: "",
        amount: 0,
        customerName: "",
        applyFee: false
      },
      stripe: null,
      clientSecret: "",
      paymentIntentId: "",
      loadingStripe: false,
      stripeError: null,
      paymentSuccess: false,
      feeAmount: 0,
      totalWithFee: 0
    };
  },
  mounted() {
    const currentUser = this.$store.getters["auth/currentUser"];
    const foundSeller = this.sellers.find(
      seller => seller.email == currentUser.email
    );
    if (foundSeller) {
      this.payment.sellerId = foundSeller.id;
    }
  },
  computed: {
    sellers() {
      return this.$store.getters["general/getSellers"];
    }
  },
  methods: {
    validateForm() {
      if (!this.payment.amount || this.payment.amount == 0) return false;
      if (!this.payment.customerName) return false;
      if (!this.payment.sellerId) return false;
      return true;
    },
    async startPayment() {
      if (!this.validateForm()) return;
      this.step = 2;
      this.loadingStripe = true;
      this.stripeError = null;

      try {
        if (!this.stripe) {
          this.stripe = await loadStripe(
            process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY
          );
        }

        // Requires backend endpoint POST /stripex/payment_intent_pos
        // Payload: { customerName, customerEmail, amount, description, sellerId, orderNumber?, orderSource }
        const response = await axios.post(
          process.env.VUE_APP_WEB_API + "/stripex/payment_intent_pos",
          {
            customerName: this.payment.customerName,
            customerEmail: this.payment.customerEmail,
            amount: parseFloat(this.payment.amount),
            description: this.payment.description,
            sellerId: this.payment.sellerId,
            orderNumber: this.payment.orderNumber || null,
            orderSource: this.payment.orderSource,
            applyFee: this.payment.applyFee
          },
          {
            headers: {
              Authorization: this.$store.getters["auth/currentToken"]
            }
          }
        );
        this.clientSecret = response.data.secret;
        this.paymentIntentId = response.data.paymentIntentId;
        this.feeAmount = response.data.feeAmount || 0;
        this.totalWithFee =
          response.data.totalWithFee || parseFloat(this.payment.amount);
      } catch (e) {
        this.stripeError =
          e.response?.data?.error ||
          e.response?.data?.message ||
          e.message ||
          "Failed to initialize payment";
        this.step = 1;
      } finally {
        this.loadingStripe = false;
      }
    },
    modifyPayment() {
      this.step = 1;
      this.stripe = null;
      this.clientSecret = "";
      this.paymentIntentId = "";
      this.stripeError = null;
      this.feeAmount = 0;
      this.totalWithFee = 0;
    },
    async onPaymentConfirmed(paymentIntentId) {
      try {
        const maxAttempts = 30;
        for (let i = 0; i < maxAttempts; i++) {
          const res = await axios.get(
            process.env.VUE_APP_WEB_API +
              `/stripex/verify_payment/${paymentIntentId}`
          );
          if (res.data.status === "succeeded") {
            this.paymentSuccess = true;
            this.$toast.open({
              message: "Payment processed successfully",
              type: "success",
              position: "top-right"
            });
            // pos_transaction_id is set by the webhook directly in pos_transactions
            this.$emit("newPaymentProcessed", res.data.pos_transaction_id);
            return;
          }
          if (res.data.status === "failed") {
            this.stripeError =
              res.data.message || "Payment verification failed.";
            return;
          }
          await new Promise(r => setTimeout(r, 1000));
        }
        this.stripeError = "Payment verification timed out.";
      } catch (e) {
        this.stripeError =
          e.response?.data?.message || "Failed to verify payment";
      }
    }
  }
};
</script>

<style lang="postcss" scoped>
form {
  display: grid;
  grid-gap: 10px;
  grid-template-columns: repeat(2, 1fr);
  margin: 0 auto;
  width: 100%;
  padding: 0% 4%;
}
.box2 {
  grid-column: 2;
}
@media screen and (max-width: 550px) {
  form {
    display: flex;
    flex-direction: column;
  }
}
</style>
